/* ==========================================================================
   Product PDP
   Scoped to [data-pdp] rather than document, so a page can hold more than one
   instance without the handlers fighting each other.

   Add to cart is deliberately NOT handled here. application.js already binds
   ajaxify.onAddToCart to '#add-to-cart-form' and opens the mini-cart drawer on
   success, so the form keeps that id and this file leaves submission alone.
   ========================================================================== */

(function () {
  'use strict';

  function initGallery(root) {
    var gallery = root.querySelector('[data-pdp-gallery]');
    var scroller = root.querySelector('[data-pdp-gallery-scroller]');
    var dotsWrap = root.querySelector('[data-pdp-gallery-dots]');
    var prevBtn = root.querySelector('[data-pdp-gallery-prev]');
    var nextBtn = root.querySelector('[data-pdp-gallery-next]');
    if (!scroller) return null;

    var items = Array.prototype.slice.call(
      scroller.querySelectorAll('.pdp-gallery-item')
    );
    var dots = dotsWrap
      ? Array.prototype.slice.call(dotsWrap.querySelectorAll('[data-pdp-gallery-dot]'))
      : [];

    if (items.length === 0) return null;

    function currentIndex() {
      var left = scroller.scrollLeft;
      var closest = 0;
      var smallest = Infinity;
      items.forEach(function (item, i) {
        var distance = Math.abs(item.offsetLeft - left);
        if (distance < smallest) {
          smallest = distance;
          closest = i;
        }
      });
      return closest;
    }

    // Dots and the prev/next arrows follow the slide in view, whether it got
    // there by arrow, dot, swipe or a variant change.
    function syncDots() {
      var active = currentIndex();
      dots.forEach(function (dot, i) {
        dot.classList.toggle('is-active', i === active);
      });
      if (prevBtn) prevBtn.disabled = active <= 0;
      if (nextBtn) nextBtn.disabled = active >= items.length - 1;
    }

    function scrollToIndex(index) {
      var target = items[index];
      if (!target) return;
      scroller.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
    }

    if (prevBtn) {
      prevBtn.addEventListener('click', function () {
        scrollToIndex(currentIndex() - 1);
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        scrollToIndex(currentIndex() + 1);
      });
    }

    dots.forEach(function (dot) {
      dot.addEventListener('click', function (event) {
        event.preventDefault();
        var index = parseInt(dot.getAttribute('data-pdp-gallery-dot'), 10);
        if (!isNaN(index)) scrollToIndex(index);
      });
    });

    var frame = null;
    scroller.addEventListener(
      'scroll',
      function () {
        if (frame !== null) return;
        frame = requestAnimationFrame(function () {
          frame = null;
          syncDots();
        });
      },
      { passive: true }
    );

    syncDots();

    return {
      // Scroll to the slide matching a variant's featured media. No-op when the
      // variant has no image assigned, so an incomplete product degrades to
      // "nothing happens" rather than jumping back to the first slide.
      showMedia: function (mediaId) {
        if (!mediaId) return;
        for (var i = 0; i < items.length; i++) {
          if (items[i].getAttribute('data-media-id') === String(mediaId)) {
            scrollToIndex(i);
            return;
          }
        }
      }
    };
  }

  function initVariants(root, gallery) {
    var inputs = Array.prototype.slice.call(
      root.querySelectorAll('.pdp-format-input')
    );
    var button = root.querySelector('[data-pdp-add]');
    var label = root.querySelector('[data-pdp-add-label]');

    if (inputs.length === 0) return;

    function onChange(input) {
      if (!input) return;

      var available = input.getAttribute('data-available') === 'true';

      if (button) button.disabled = !available;
      if (label) {
        // data-add-label carries "Pre-order · out 17 Nov" before release.
        var addLabel = (button && button.getAttribute('data-add-label')) || 'Add to cart';
        label.textContent = available ? addLabel : 'Sold out';
      }

      // Lets the sticky mobile bar follow the selected format.
      root.dispatchEvent(new CustomEvent('pdp:variant-change', { detail: { input: input } }));

      if (gallery) gallery.showMedia(input.getAttribute('data-media-id'));

      // Keep the URL shareable without adding a history entry per click.
      if (window.history && window.history.replaceState) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', input.value);
        window.history.replaceState({}, '', url.toString());
      }
    }

    inputs.forEach(function (input) {
      input.addEventListener('change', function () {
        if (input.checked) onChange(input);
      });
    });

    // Sync the gallery to whatever is checked on load, so a ?variant= deep link
    // opens on the right image.
    var checked = inputs.filter(function (input) {
      return input.checked;
    })[0];
    if (checked && gallery) {
      gallery.showMedia(checked.getAttribute('data-media-id'));
    }
  }

  function initSpecs(root) {
    var toggle = root.querySelector('[data-pdp-specs-toggle]');
    var list = root.querySelector('[data-pdp-specs]');
    var label = root.querySelector('[data-pdp-specs-toggle-label]');

    if (!toggle || !list) return;

    // Uses the `hidden` attribute rather than a class so collapsed specs stay
    // out of the accessibility tree, with aria-expanded kept in step.
    // With GSAP the list's height eases open and shut, pushing the button
    // smoothly rather than jumping it.
    var isOpen = false;

    toggle.addEventListener('click', function () {
      isOpen = !isOpen;
      toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      if (label) label.textContent = isOpen ? 'View less' : 'View more';

      if (typeof gsap === 'undefined' || reduceMotion) {
        list.hidden = !isOpen;
        return;
      }

      var from = list.hidden ? 0 : list.offsetHeight;
      list.hidden = false;
      list.style.overflow = 'hidden';

      if (isOpen) {
        gsap.set(list, { height: 'auto' });
        var to = list.offsetHeight;
        gsap.fromTo(list, { height: from }, {
          height: to, duration: easeDuration(to - from), ease: 'power2.inOut', overwrite: true,
          onComplete: function () {
            gsap.set(list, { clearProps: 'height' });
            list.style.overflow = '';
          }
        });
      } else {
        gsap.fromTo(list, { height: from }, {
          height: 0, duration: easeDuration(from), ease: 'power2.inOut', overwrite: true,
          onComplete: function () {
            list.hidden = true;
            gsap.set(list, { clearProps: 'height' });
            list.style.overflow = '';
          }
        });
      }
    });
  }

  // Height tweens scale gently with distance: short reveals stay snappy,
  // long ones get a little longer, so they all feel like the same motion.
  function easeDuration(distance) {
    return Math.min(0.85, Math.max(0.45, 0.4 + Math.abs(distance) / 1400));
  }

  function initTrailer(root) {
    var popup = root.querySelector('[data-pdp-video-popup]');
    var iframe = root.querySelector('[data-pdp-video-iframe]');
    // The gallery's trailer slide and the "Watch trailer" pill both open it.
    var openers = Array.prototype.slice.call(root.querySelectorAll('[data-pdp-trailer-open]'));
    var closeBtn = root.querySelector('[data-pdp-video-close]');
    var lastOpener = null;

    if (!popup || !iframe || openers.length === 0) return;

    function open() {
      iframe.src = iframe.getAttribute('data-embed-src');
      popup.hidden = false;
      document.documentElement.style.overflow = 'hidden';
      if (closeBtn) closeBtn.focus();
    }

    function close() {
      popup.hidden = true;
      iframe.src = ''; // stops playback
      document.documentElement.style.overflow = '';
      if (lastOpener) lastOpener.focus();
    }

    openers.forEach(function (opener) {
      opener.addEventListener('click', function () {
        lastOpener = opener;
        open();
      });
    });
    if (closeBtn) closeBtn.addEventListener('click', close);

    popup.addEventListener('click', function (event) {
      if (event.target === popup) close();
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && !popup.hidden) close();
    });
  }

  // Add to cart hover, GSAP-driven (loaded globally in theme.liquid). Kept
  // deliberately calm:
  //   - the pink fades in softly, and fades out again on leave
  //   - the arrow nudges a few pixels up-right
  //   - pressing dips the button very slightly, with no spring
  // Mouse only; without GSAP or with reduced motion the CSS hover applies.
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function initAddButton(root) {
    var button = root.querySelector('[data-pdp-add]');
    if (!button || typeof gsap === 'undefined' || reduceMotion) return;

    var fill = button.querySelector('.pdp-add-fill');
    var arrow = button.querySelector('.pdp-add-arrow');
    if (!fill) return;

    button.classList.add('pdp-add--gsap');
    gsap.set(fill, { scaleX: 1, opacity: 0 });
    gsap.set(button, { force3D: true });

    function fillIn() {
      gsap.to(fill, { opacity: 1, duration: 0.2, ease: 'power1.out', overwrite: true });

      if (arrow) {
        gsap.to(arrow, { x: 3, y: -3, duration: 0.35, ease: 'power2.out', overwrite: true });
      }
    }

    function fillOut() {
      gsap.to(fill, { opacity: 0, duration: 0.2, ease: 'power1.out', overwrite: true });
      if (arrow) gsap.to(arrow, { x: 0, y: 0, duration: 0.3, ease: 'power2.out', overwrite: true });
    }

    button.addEventListener('pointerenter', function (event) {
      if (event.pointerType === 'mouse' && !button.disabled) fillIn();
    });

    button.addEventListener('pointerleave', function (event) {
      if (event.pointerType !== 'mouse') return;
      fillOut();
      gsap.to(button, { scale: 1, duration: 0.3, ease: 'power2.out', overwrite: 'auto' });
    });

    button.addEventListener('pointerdown', function () {
      if (!button.disabled) gsap.to(button, { scale: 0.985, duration: 0.12, ease: 'power2.out', overwrite: 'auto' });
    });

    button.addEventListener('pointerup', function () {
      gsap.to(button, { scale: 1, duration: 0.3, ease: 'power2.out', overwrite: 'auto' });
    });

    // Switching to a sold-out format while hovering disables the button;
    // clear the fill so "Sold out" doesn't sit on pink.
    new MutationObserver(function () {
      if (button.disabled) fillOut();
    }).observe(button, { attributes: true, attributeFilter: ['disabled'] });
  }

  // Sticky mobile add to cart. Shown once the real button has scrolled above
  // the viewport; GSAP slides it up and down. The bar's button clicks the
  // real one rather than submitting anything itself.
  function initSticky(root) {
    var bar = root.querySelector('[data-pdp-sticky]');
    var stickyBtn = root.querySelector('[data-pdp-sticky-add]');
    var variantText = root.querySelector('[data-pdp-sticky-variant]');
    var mainBtn = root.querySelector('[data-pdp-add]');
    if (!bar || !stickyBtn || !mainBtn || !('IntersectionObserver' in window)) return;

    // Single-column layouts (tablets and phones), matching product-pdp.css
    var mobile = window.matchMedia('(max-width: 899px)');
    var useGsap = typeof gsap !== 'undefined' && !reduceMotion;
    var shown = false;
    var buttonAbove = false;
    var footerInView = false;

    if (useGsap) {
      bar.classList.add('pdp-sticky--gsap');
      gsap.set(bar, { yPercent: 110, force3D: true });
    }

    function setShown(next) {
      if (next === shown) return;
      shown = next;
      bar.setAttribute('aria-hidden', next ? 'false' : 'true');
      stickyBtn.tabIndex = next ? 0 : -1;
      bar.classList.toggle('is-visible', next);
      if (useGsap) {
        gsap.to(bar, {
          yPercent: next ? 0 : 110,
          duration: next ? 0.5 : 0.35,
          ease: next ? 'expo.out' : 'power2.in',
          overwrite: true
        });
      }
    }

    function update() {
      setShown(mobile.matches && buttonAbove && !footerInView);
    }

    // Only once the button is above the viewport, not before reaching it.
    new IntersectionObserver(function (entries) {
      var entry = entries[0];
      buttonAbove = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      update();
    }).observe(mainBtn);

    // Out of the way once the footer arrives, so it doesn't cover its links.
    var footer = document.getElementById('footer-global');
    if (footer) {
      new IntersectionObserver(function (entries) {
        footerInView = entries[0].isIntersecting;
        update();
      }).observe(footer);
    }

    mobile.addEventListener('change', function () {
      if (!mobile.matches) setShown(false);
    });

    stickyBtn.addEventListener('click', function () {
      if (!mainBtn.disabled) mainBtn.click();
    });

    root.addEventListener('pdp:variant-change', function (event) {
      var input = event.detail.input;
      var option = input && root.querySelector('label[for="' + input.id + '"]');
      var available = input.getAttribute('data-available') === 'true';

      stickyBtn.disabled = !available;
      stickyBtn.textContent = available ? bar.getAttribute('data-sticky-label') : 'Sold out';

      if (option && variantText) {
        var name = option.querySelector('.pdp-format-name');
        // The current price (any struck-through old price sits beside it)
        var price = option.querySelector('.pdp-format-now');
        var now = price ? price.textContent.trim() : '';
        variantText.textContent = (name ? name.textContent.trim() + ' · ' : '') + now;
      }
    });
  }

  function init() {
    var roots = document.querySelectorAll('[data-pdp]');
    Array.prototype.forEach.call(roots, function (root) {
      if (root.dataset.pdpBound === 'true') return;
      root.dataset.pdpBound = 'true';

      var gallery = initGallery(root);
      initVariants(root, gallery);
      initSpecs(root);
      initTrailer(root);
      initAddButton(root);
      initSticky(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-run when a section is re-rendered in the theme editor.
  document.addEventListener('shopify:section:load', init);
})();